import React, { useState, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage, validateYupSchema } from "formik";
import { Container, Row, Col, Button, Card } from "react-bootstrap";
import * as Yup from "yup";
import "./adddata.css";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import { Typography, Divider, Box } from "@mui/material";

import { useDispatch, useSelector } from "react-redux";
import { FadeLoader } from "react-spinners";
import GeneralApiService from "../../../services/generalServices";
import translations from "../../../assets/locals/translations";
import DynamicFormikForm from "../../stages/DynamicFormikForm";
import { setShowMessage } from "../../../redux/slices/notificationSlice";

const transformData = (fields) => {
  const groupedData = fields.reduce((acc, field) => {
    const { main_field_name, ...fieldData } = field;

    if (!acc[main_field_name]) {
      acc[main_field_name] = {
        main_field_name: main_field_name,
        fields: [],
      };
    }

    acc[main_field_name].fields.push(fieldData);
    return acc;
  }, {});

  // Convert the object back to an array for easier rendering with FlatList
  return Object.values(groupedData);
};

/** Ensures SSP dropdown exists for Add Data (same behaviour as New Enquiry) for every category. */
const injectSspFieldForAddData = (groupedFields) => {
  if (!Array.isArray(groupedFields) || groupedFields.length === 0) {
    return groupedFields;
  }
  const hasSsp = groupedFields.some((section) =>
    (section.fields || []).some((f) => f && f.name === "ssp")
  );
  if (hasSsp) return groupedFields;

  const sspField = { name: "ssp", label: "SSP", type: "dropdown" };
  const dspSectionIdx = groupedFields.findIndex((section) =>
    (section.fields || []).some((f) => f && f.name === "dsp")
  );
  if (dspSectionIdx >= 0) {
    return groupedFields.map((section, i) => {
      if (i !== dspSectionIdx) return section;
      const fs = [...(section.fields || [])];
      const dspIdx = fs.findIndex((f) => f && f.name === "dsp");
      const insertAt = dspIdx >= 0 ? dspIdx + 1 : fs.length;
      fs.splice(insertAt, 0, sspField);
      return { ...section, fields: fs };
    });
  }
  return [
    ...groupedFields,
    { main_field_name: "Sales Support Person", fields: [sspField] },
  ];
};

const AddData = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const preselectCategoryId = location.state?.preselectCategoryId;
  const dispatch = useDispatch();
  const currentLanguage = useSelector((state) => state.language.language);
  const t = translations[currentLanguage] || translations["en"];
  const generalServices = new GeneralApiService();
  const [selectedValue, setSelectedValue] = useState(null);
  const [categoryList, setCategoryList] = useState([]);
  const [optionsData, setOptionsData] = useState({});
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sspOptions, setSspOptions] = useState([]);

  const getSspList = async () => {
    try {
      const response = await generalServices.getSspList();
      if (response && response.isSuccess === true && Array.isArray(response.result)) {
        const formatted = response.result.map((item) => ({
          value: item.ssp_id,
          label: item.first_name,
        }));
        setSspOptions(formatted);
        return formatted;
      }
    } catch (error) {
      console.log(error, "Error fetching SSP list");
    }
    return [];
  };

  const getCategoryList = async () => {
    try {
      await generalServices.getCategoryList().then((response) => {
        if (response && response.isSuccess) {
          setCategoryList(response.result.filter((val) => val.value > 3));
        } else {
          setCategoryList([]);
        }
      });
    } catch (error) {
      console.log(error, "Error getting category");
    }
  };

  const getFields = async (selectedCategory, sspOptionsOverride = null) => {
    try {
      setLoading(true);
      const response = await generalServices.getCategoryFields(
        selectedCategory
      );
      const opts = sspOptionsOverride ?? sspOptions;
      const updatedData = {
        ...response.data,
        categoryId: selectedCategory,
        sspOptions: opts,
      };
      if (response && response.isSuccess) {
        setOptionsData(updatedData);
        const updatedFields = response.result[selectedCategory].map((field) => {
          return field;
        });

        const transformedData = injectSspFieldForAddData(
          transformData(updatedFields)
        );

        return transformedData || [];
      } else {
        console.log("Invalid response or no fields found");
        return [];
      }
    } catch (error) {
      console.error("Error retrieving fields:", error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      await Promise.all([getCategoryList(), getSspList()]);
    };
    fetchData();
  }, []);

  useEffect(() => {
    setOptionsData((prev) => ({
      ...prev,
      sspOptions,
    }));
  }, [sspOptions]);

  useEffect(() => {
    const applyPreselect = async () => {
      if (preselectCategoryId == null || preselectCategoryId === "") return;
      const id = preselectCategoryId;
      setSelectedValue(String(id));
      const opts = sspOptions.length > 0 ? sspOptions : await getSspList();
      const nextFields = await getFields(id, opts);
      setFields(nextFields || []);
    };
    if (categoryList.length > 0 && preselectCategoryId != null) {
      applyPreselect();
    }
  }, [categoryList, preselectCategoryId]);

  const handleFormSubmit = async (values) => {
    console.log("Form submitted with values:", values);

    const selectedCategoryObj = categoryList.find(
      (c) => c.value == selectedValue
    );

    values["categoryId"] = selectedValue;
    values["categoryName"] = selectedCategoryObj?.label; // ✅ ADD THIS
    values["customer_type"] = "Data";

    const response = await generalServices.AddData(values);

    if (response && response.isSuccess) {
      setFields([]);
      setSelectedValue(null);
      dispatch(setShowMessage("Data Added successfully!"));
      navigate("/sale/data");
    } else {
      dispatch(setShowMessage(response.message));
    }
  };
  return (
    <>
      <Container className="bg-white rounded">
        <Row className="justify-content-md-center">
          <Col md={12}>
            <Formik
              enableReinitialize={true}
              initialValues={{ category: selectedValue }}
              validationSchema={Yup.object().shape({
                category: Yup.string().required("Category is required"),
              })}
            >
              {({ isSubmitting, values, touched, errors, setFieldValue }) => {
                const handleCategoryChange = async (e) => {
                  let value = e.target.value;
                  try {
                    setFieldValue("category", value);
                    setSelectedValue(value);
                    if (value) {
                      const next = await getFields(value, sspOptions);
                      setFields(next);
                    }
                  } catch (error) {
                    console.log(error, "Error during category change");
                  }
                };
                return (
                  <Form>
                    <Row className="mb-1">
                      <Col md={12}>
                        <Box className="data-header">
                          <Typography variant="h6" className="data-title">
                            {t.adddata || "Add Data"}
                          </Typography>
                          <Box className="header-divider" />
                        </Box>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={4}>
                        <label htmlFor="category" className="form-label">
                          {t.selectcategory || "Select Category"} *
                        </label>
                        <Field
                          as="select"
                          name="category"
                          className="form-control form-select"
                          onChange={(e) => handleCategoryChange(e)}
                        >
                          <option value="" label={t.selectcategory || "Select Category"} />
                          {categoryList.map((category) => (
                            <option
                              key={category.value}
                              value={category.value}
                              label={category.label}
                            />
                          ))}
                        </Field>
                        <ErrorMessage
                          name="category"
                          component={({ children }) => (
                            <span
                              style={{ color: "darkred", fontSize: "0.875rem" }}
                            >
                              {children}
                            </span>
                          )}
                        />
                      </Col>
                    </Row>
                    <hr />
                  </Form>
                );
              }}
            </Formik>

            {loading ? (
              <div
                className="container"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                }}
              >
                <FadeLoader size={10} color="#808b96" />
                <span
                  style={{
                    // marginTop: "10px",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "16px",
                    color: "#808b96",
                  }}
                >
                  {t.loadingPleaseWait || "Preparing, please wait..."}
                </span>
              </div>
            ) : selectedValue ? (
              <DynamicFormikForm
                key={selectedValue}
                fields={fields}
                optionsData={optionsData}
                submitLabel={t.submit || "Submit"}
                onSubmit={handleFormSubmit}
              />
            ) : (
              <div className="notification-container d-flex flex-column align-items-center justify-content-center text-center">
                <div className="icon-wrapper">
                  <i
                    className="fa fa-file-text-o"
                    style={{ fontSize: "120px", color: "#007aff" }}
                  ></i>
                </div>
                <p
                  className="message"
                  style={{ fontSize: "16px", color: "#007aff", textTransform: 'uppercase', fontWeight: 'bold' }}
                >
                  {t.selectdatatype || "Select a data type to proceed."}
                </p>
              </div>
            )}
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default AddData;
